import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useOutlookCalendar } from '@/hooks/useOutlookCalendar';
import { supabase } from '@/integrations/supabase/client';
import { TrajectPicker } from '@/components/crm/TrajectPicker';
import { trajectPatch, type TrajectPatch } from '@/lib/traject';

interface EventModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

export function EventModal({ open, onOpenChange, onSuccess }: EventModalProps) {
  const { createEvent } = useOutlookCalendar();
  const [formData, setFormData] = useState({
    subject: '',
    startTime: '',
    endTime: '',
    location: '',
    body: '',
    attendees: ''
  });
  const [submitting, setSubmitting] = useState(false);
  // T2.3 - migratie 033 gaf afspraken een traject-as. Hoogstens een van de drie
  // is gevuld; de picker garandeert dat, de CHECK vangt de rest af.
  const [traject, setTraject] = useState<TrajectPatch>(trajectPatch('geen', null));

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      const event = await createEvent({
        subject: formData.subject,
        startTime: formData.startTime,
        endTime: formData.endTime,
        location: formData.location || undefined,
        body: formData.body || undefined,
        attendees: formData.attendees ? formData.attendees.split(',').map(e => e.trim()) : undefined
      });

      // De edge function maakt de afspraak in Graph en schrijft de rij weg, maar
      // kent het traject niet. Daarom hier na afloop koppelen, op (user_id,
      // event_id) - sinds migratie 036 is dat de unieke sleutel, en zonder
      // user_id zou dit de rij van een collega kunnen raken.
      const heeftTraject =
        traject.project_id || traject.charging_location_id || traject.charging_opportunity_id;
      if (heeftTraject && event?.id) {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase
            .from('microsoft_calendar_events')
            .update(traject as any)
            .eq('user_id', user.id)
            .eq('event_id', event.id);
        }
      }

      onOpenChange(false);
      setFormData({ subject: '', startTime: '', endTime: '', location: '', body: '', attendees: '' });
      setTraject(trajectPatch('geen', null));
      onSuccess?.();
    } catch (error) {
      // Error already handled in hook
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Nieuwe afspraak</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <TrajectPicker
            projectId={traject.project_id}
            chargingLocationId={traject.charging_location_id}
            chargingOpportunityId={traject.charging_opportunity_id}
            onChange={(patch) => setTraject(patch)}
          />
          <div>
            <Label htmlFor="subject">Onderwerp</Label>
            <Input 
              id="subject"
              required 
              value={formData.subject} 
              onChange={e => setFormData({...formData, subject: e.target.value})} 
              placeholder="Bijv. Overleg met klant"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="startTime">Start</Label>
              <Input 
                id="startTime"
                type="datetime-local" 
                required 
                value={formData.startTime} 
                onChange={e => setFormData({...formData, startTime: e.target.value})} 
              />
            </div>
            <div>
              <Label htmlFor="endTime">Eind</Label>
              <Input 
                id="endTime"
                type="datetime-local" 
                required 
                value={formData.endTime} 
                onChange={e => setFormData({...formData, endTime: e.target.value})} 
              />
            </div>
          </div>
          <div>
            <Label htmlFor="location">Locatie</Label>
            <Input 
              id="location"
              value={formData.location} 
              onChange={e => setFormData({...formData, location: e.target.value})} 
              placeholder="Bijv. Kantoor Eindhoven"
            />
          </div>
          <div>
            <Label htmlFor="attendees">Deelnemers (emails, gescheiden door komma)</Label>
            <Input 
              id="attendees"
              placeholder="niels@greenvision.energy, stan@greenvision.energy" 
              value={formData.attendees} 
              onChange={e => setFormData({...formData, attendees: e.target.value})} 
            />
          </div>
          <div>
            <Label htmlFor="body">Beschrijving</Label>
            <Textarea 
              id="body"
              value={formData.body} 
              onChange={e => setFormData({...formData, body: e.target.value})} 
              placeholder="Voeg extra informatie toe..."
              rows={4}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuleren
            </Button>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Aanmaken...' : 'Afspraak aanmaken'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
